import { createElement } from 'react';
import { Pressable, View } from 'react-native';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';

import { Text } from '@/components/ui';
import { libraryFilterHref } from '@/lib/library/library-href';
import { parseLibraryFilter } from '@/lib/library/types';

import {
  SidebarFinanceIcon,
  SidebarLibraryIcon,
  SidebarSettingsIcon,
  SidebarTrashIcon,
} from './SidebarIcons';

type FooterItem = {
  key: string;
  label: string;
  href: string;
  icon: () => ReturnType<typeof createElement>;
  isActive: (pathname: string, filter: string | undefined) => boolean;
};

const FOOTER_ITEMS: FooterItem[] = [
  {
    key: 'library',
    label: 'Library',
    href: libraryFilterHref('recents'),
    icon: SidebarLibraryIcon,
    isActive: (pathname, filter) =>
      pathname === '/library' && parseLibraryFilter(filter) !== 'trash',
  },
  {
    key: 'finance',
    label: 'Finance',
    href: '/finance/transactions',
    icon: SidebarFinanceIcon,
    isActive: (pathname) => pathname.startsWith('/finance'),
  },
  {
    key: 'trash',
    label: 'Trash',
    href: libraryFilterHref('trash'),
    icon: SidebarTrashIcon,
    isActive: (pathname, filter) =>
      pathname === '/library' && parseLibraryFilter(filter) === 'trash',
  },
  {
    key: 'settings',
    label: 'Settings',
    href: '/settings',
    icon: SidebarSettingsIcon,
    isActive: (pathname) => pathname.startsWith('/settings'),
  },
];

export function SidebarFooter() {
  const router = useRouter();
  const pathname = usePathname();
  const { filter: filterParam } = useLocalSearchParams<{ filter?: string }>();
  const filter = typeof filterParam === 'string' ? filterParam : filterParam?.[0];

  return (
    <View className="shrink-0 border-t border-border px-1.5 py-1">
      {FOOTER_ITEMS.map((item) => {
        const active = item.isActive(pathname, filter);
        return (
          <Pressable
            key={item.key}
            className={`sidebar-footer-row mb-0.5 w-full flex-row items-center justify-start gap-1.5 rounded px-1 py-0.5 ${
              active ? 'bg-surface' : ''
            }`}
            onPress={() => router.push(item.href)}
          >
            <View className="shrink-0">{createElement(item.icon)}</View>
            <Text variant="body-sm" className={`shrink text-left ${active ? 'font-medium' : ''}`}>
              {item.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
